import React, { useEffect, useMemo, useState } from 'react';
import {
  FlowExample, FlowGraphData, FlowNode, FlowParam, FlowSubstep, LANE_LABELS, TraceStep, asList, fmtMs, num,
  statusStyle,
} from './flowTypes';
import { KIND_STYLE, Selection } from './FlowGraph';

/**
 * The side panel of the Search-flow tab: what a node (or one of its
 * sub-steps, or an edge) does, its live params with where each value comes
 * from, its example — and, once a live query has run, what THIS run fed it,
 * what it produced and how long it took. Prev / Next walk the current path
 * one step (and sub-step) at a time.
 */

export interface NavItem { id: string; sub?: string; label: string }

interface Props {
  graph: FlowGraphData;
  sel: Selection;
  onSelect: (s: Selection) => void;
  onClose: () => void;
  /** node-level steps of the current trace, by node id (visit order) */
  nodeSteps: Map<string, TraceStep[]>;
  /** sub-step steps of the current trace, `${node}/${sub}` */
  subSteps: Map<string, TraceStep>;
  hasTrace: boolean;
  view: 'run' | 'generic';
  setView: (v: 'run' | 'generic') => void;
  nav: { prev: NavItem | null; next: NavItem | null; pos: number; total: number; label: string };
  pathNodes: Set<string>;
}

// ---------------------------------------------------------------------------
// small atoms
// ---------------------------------------------------------------------------
const SOURCE_CLS: Record<string, string> = {
  setting: 'bg-primary-100 text-primary-700 border-primary-300',
  env: 'bg-purple-50 text-purple-700 border-purple-200',
  code: 'bg-grayscale-200 text-grayscale-1100 border-grayscale-400',
  live: 'bg-green-50 text-green-700 border-green-300',
};
const SOURCE_TIP: Record<string, string> = {
  setting: 'Admin setting (app_settings.search_v2) — changeable in Search settings',
  env: 'Environment variable on the server',
  code: 'Constant in the code — changes need a deploy',
  live: 'Measured / read live right now',
};

export const SourceBadge: React.FC<{ source?: string }> = ({ source }) => {
  const s = String(source || 'code');
  return (
    <span title={SOURCE_TIP[s] || s}
      className={`inline-block rounded-full border px-1.5 py-px text-[10px] font-medium whitespace-nowrap ${SOURCE_CLS[s] || SOURCE_CLS.code}`}>
      {s}
    </span>
  );
};

export const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  const s = statusStyle(status);
  return (
    <span className={`inline-block rounded-full border px-2 py-px text-[10px] font-bold uppercase tracking-wide ${s.cls}`}>
      {String(status || 'ran')}
    </span>
  );
};

export const fmtVal = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (typeof v === 'number') return v.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (Array.isArray(v)) {
    if (!v.length) return '[ ]';
    return v.every(x => x === null || typeof x !== 'object') ? v.join(', ') : JSON.stringify(v);
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export const JsonBlock: React.FC<{ value: any; label?: string; maxH?: string }> = ({ value, label, maxH = '18rem' }) => {
  const text = useMemo(() => {
    if (value === undefined) return '';
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value, null, 2); } catch { return String(value); }
  }, [value]);
  const [copied, setCopied] = useState(false);
  const empty = value === undefined || value === null || text === '' || text === '{}' || text === '[]';
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {label && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">{label}</span>
          {!empty && (
            <button type="button" className="text-[11px] text-primary-700 underline underline-offset-2"
              onClick={() => {
                try { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); }
                catch { /* clipboard blocked: nothing to do */ }
              }}>
              {copied ? 'copied' : 'copy'}
            </button>
          )}
        </div>
      )}
      {empty
        ? <div className="text-xs text-grayscale-1100 italic">— nothing —</div>
        : <pre className="text-[11px] leading-snug font-mono bg-grayscale-200 rounded-lg p-2 overflow-auto whitespace-pre-wrap break-words"
            style={{ maxHeight: maxH }}>{text}</pre>}
    </div>
  );
};

export const ParamTable: React.FC<{ params?: FlowParam[] | null }> = ({ params }) => {
  const list = (params || []).filter(Boolean);
  if (!list.length) return <div className="text-xs text-grayscale-1100 italic">No parameters.</div>;
  return (
    <div className="overflow-x-auto rounded-lg border border-grayscale-300">
      <table className="w-full text-xs">
        <thead className="bg-grayscale-200">
          <tr>
            <th className="text-left px-2 py-1.5 font-grotesk text-grayscale-1200">Param</th>
            <th className="text-left px-2 py-1.5 font-grotesk text-grayscale-1200">Value</th>
            <th className="text-left px-2 py-1.5 font-grotesk text-grayscale-1200">Source</th>
          </tr>
        </thead>
        <tbody>
          {list.map((p, i) => {
            const val = fmtVal(p.value);
            const long = val.length > 80;
            return (
              <tr key={`${p.key}-${i}`} className="border-t border-grayscale-200 align-top">
                <td className="px-2 py-1.5 min-w-[8rem]">
                  <div className="text-grayscale-1300 font-medium">{p.label || p.key}</div>
                  {p.label && <div className="font-mono text-[10px] text-grayscale-1100 break-all">{p.key}</div>}
                  {p.note && <div className="text-[10.5px] text-grayscale-1100 mt-0.5 leading-snug">{p.note}</div>}
                </td>
                <td className="px-2 py-1.5 text-grayscale-1300 break-words">
                  {long
                    ? <details><summary className="cursor-pointer text-primary-700">{val.slice(0, 60)}…</summary>
                        <JsonBlock value={p.value} maxH="12rem" /></details>
                    : <span className="font-mono text-[11.5px]">{val}{p.unit && val !== '—' ? <span className="text-grayscale-1100"> {p.unit}</span> : null}</span>}
                </td>
                <td className="px-2 py-1.5"><SourceBadge source={p.source} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const ExampleView: React.FC<{ example?: FlowExample | null }> = ({ example }) => {
  if (!example || (example.input === undefined && example.output === undefined && !example.note)) {
    return <div className="text-xs text-grayscale-1100 italic">No example for this step.</div>;
  }
  return (
    <div className="flex flex-col gap-2">
      {(example.source || example.note) && (
        <div className="text-[11.5px] text-grayscale-1100 leading-snug">
          {example.source && <span className="inline-block rounded-full bg-grayscale-200 px-2 py-px mr-1.5 text-[10.5px]">{example.source}</span>}
          {example.note}
        </div>
      )}
      <div className="grid grid-cols-1 gap-2">
        <JsonBlock label="Input" value={example.input} maxH="14rem" />
        <JsonBlock label="Output" value={example.output} maxH="16rem" />
      </div>
    </div>
  );
};

const Notes: React.FC<{ notes: any }> = ({ notes }) => {
  const list = asList(notes);
  if (!list.length) return null;
  return (
    <ul className="list-disc pl-5 text-[12px] text-grayscale-1200 flex flex-col gap-0.5">
      {list.map((n, i) => <li key={i}>{n}</li>)}
    </ul>
  );
};

export const StepView: React.FC<{ step: TraceStep; compact?: boolean }> = ({ step, compact }) => {
  const t0 = num(step.t_start_ms); const ms = num(step.ms);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1200">
        <StatusBadge status={step.status} />
        <span>took <b className="text-grayscale-1300">{fmtMs(ms)}</b></span>
        {t0 !== null && <span className="text-grayscale-1100">started at +{fmtMs(t0)}{ms !== null ? `, ended +${fmtMs(t0 + ms)}` : ''}</span>}
      </div>
      <Notes notes={step.notes} />
      <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-1'}`}>
        <JsonBlock label="Input (this run)" value={step.input} maxH={compact ? '10rem' : '14rem'} />
        <JsonBlock label="Output (this run)" value={step.output} maxH={compact ? '12rem' : '20rem'} />
      </div>
    </div>
  );
};

const H: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="flex items-center justify-between gap-2 mt-1">
    <h4 className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">{children}</h4>
    {right}
  </div>
);

// ---------------------------------------------------------------------------
// the panel
// ---------------------------------------------------------------------------
const FlowNodePanel: React.FC<Props> = ({
  graph, sel, onSelect, onClose, nodeSteps, subSteps, hasTrace, view, setView, nav, pathNodes,
}) => {
  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({});
  const selKey = sel.kind === 'edge' ? `e:${sel.id}` : `n:${sel.id}/${sel.sub || ''}`;
  useEffect(() => { setOpenSubs({}); }, [selKey]);

  const nodeById = useMemo(() => new Map(graph.nodes.map(n => [n.id, n])), [graph]);

  const header = (kind: string, lane: string | null, title: React.ReactNode, code?: string) => {
    const ks = KIND_STYLE[kind] || KIND_STYLE.step;
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="rounded-full border px-2 py-px text-[10px] font-bold uppercase tracking-wide"
              style={{ background: ks.fill, borderColor: ks.stroke, color: '#3D3D3D' }}>{kind}</span>
            {lane && <span className="rounded-full bg-grayscale-200 px-2 py-px text-[10px] uppercase tracking-wide text-grayscale-1100">
              {LANE_LABELS[lane] || lane}</span>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close details"
            className="shrink-0 w-7 h-7 rounded-full text-grayscale-1100 hover:bg-grayscale-200 text-lg leading-none">×</button>
        </div>
        <h3 className="text-base font-bold text-grayscale-1300 font-grotesk leading-tight">{title}</h3>
        {code && (
          <div className="flex flex-wrap gap-1">
            {code.split(/\s*[;,]\s*(?=[\w./]+\.(?:py|tsx?)\b)/).map(c => (
              <code key={c} className="text-[11px] font-mono bg-grayscale-200 text-grayscale-1200 rounded px-1.5 py-0.5 break-all">{c}</code>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs">
          <button type="button" disabled={!nav.prev} onClick={() => nav.prev && onSelect({ kind: 'node', id: nav.prev.id, sub: nav.prev.sub })}
            title={nav.prev ? `Previous: ${nav.prev.label} (←)` : ''}
            className="px-2.5 py-1 rounded-full border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 disabled:opacity-40">
            ‹ Prev
          </button>
          <span className="text-grayscale-1100 flex-1 text-center truncate" title={nav.label}>
            {nav.pos > 0 ? `${nav.pos} / ${nav.total} · ${nav.label}` : nav.label}
          </span>
          <button type="button" disabled={!nav.next} onClick={() => nav.next && onSelect({ kind: 'node', id: nav.next.id, sub: nav.next.sub })}
            title={nav.next ? `Next: ${nav.next.label} (→)` : ''}
            className="px-2.5 py-1 rounded-full border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 disabled:opacity-40">
            Next ›
          </button>
        </div>
      </div>
    );
  };

  // --- an edge -------------------------------------------------------------
  if (sel.kind === 'edge') {
    const e = graph.edges.find(x => x.id === sel.id);
    if (!e) return null;
    const a = nodeById.get(e.from); const b = nodeById.get(e.to);
    return (
      <div className="flex flex-col gap-3">
        {header(e.kind === 'fallback' ? 'fallback edge' : e.kind === 'loop' ? 'loop edge' : 'edge', null,
          e.label || `${a?.label || e.from} → ${b?.label || e.to}`)}
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <button type="button" className="underline underline-offset-2 text-primary-700"
            onClick={() => onSelect({ kind: 'node', id: e.from })}>{a?.label || e.from}</button>
          <span className="text-grayscale-1100">→</span>
          <button type="button" className="underline underline-offset-2 text-primary-700"
            onClick={() => onSelect({ kind: 'node', id: e.to })}>{b?.label || e.to}</button>
        </div>
        {e.condition && (<><H>Taken when</H><p className="text-[13px] text-grayscale-1200 whitespace-pre-line">{e.condition}</p></>)}
        <div className="text-[11px] text-grayscale-1100 font-mono">id {e.id}</div>
      </div>
    );
  }

  const node = nodeById.get(sel.id);
  if (!node) return null;
  const sub: FlowSubstep | undefined = sel.sub ? (node.substeps || []).find(s => s.id === sel.sub) : undefined;
  const ownSteps = nodeSteps.get(node.id) || [];
  const subStepList = (node.substeps || [])
    .map(s => ({ s, step: subSteps.get(`${node.id}/${s.id}`) }))
    .filter(x => !!x.step) as { s: FlowSubstep; step: TraceStep }[];
  const thisSubStep = sub ? subSteps.get(`${node.id}/${sub.id}`) : undefined;
  const runAvailable = hasTrace && (sub ? !!thisSubStep : (ownSteps.length > 0 || subStepList.length > 0));
  const showRun = runAvailable && view === 'run';
  const offPath = hasTrace && !pathNodes.has(node.id);

  const out = graph.edges.filter(e => e.from === node.id);
  const inc = graph.edges.filter(e => e.to === node.id && e.from !== node.id);   // a self-loop is listed once, as outgoing

  const toggle = runAvailable && (
    <div className="inline-flex rounded-full border border-grayscale-400 p-0.5 text-xs self-start">
      {(['run', 'generic'] as const).map(v => (
        <button key={v} type="button" onClick={() => setView(v)}
          className={`px-3 py-1 rounded-full ${view === v ? 'bg-secondary-500 text-white font-bold' : 'text-grayscale-1200'}`}>
          {v === 'run' ? 'This run' : 'How it works'}
        </button>
      ))}
    </div>
  );

  // --- a sub-step ------------------------------------------------------------
  if (sub) {
    return (
      <div className="flex flex-col gap-3">
        {header('sub-step', node.lane, (
          <span>
            <button type="button" className="text-primary-700 hover:underline" onClick={() => onSelect({ kind: 'node', id: node.id })}>
              {node.label}
            </button>
            <span className="text-grayscale-1100"> › </span>{sub.label}
          </span>
        ), sub.code || node.code)}
        <div className="flex gap-1 flex-wrap">
          {(node.substeps || []).map(s => (
            <button key={s.id} type="button" onClick={() => onSelect({ kind: 'node', id: node.id, sub: s.id })}
              className={`px-2.5 py-1 rounded-full text-[11px] border ${s.id === sub.id
                ? 'bg-primary-500 text-white border-primary-500 font-bold'
                : 'border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
              {s.label}
              {subSteps.get(`${node.id}/${s.id}`) && (
                <span className="ml-1 inline-block w-2 h-2 rounded-full align-middle"
                  style={{ background: statusStyle(subSteps.get(`${node.id}/${s.id}`)!.status).stroke }} />
              )}
            </button>
          ))}
        </div>
        {toggle}
        {showRun && thisSubStep ? <StepView step={thisSubStep} /> : (
          <>
            {sub.summary && <p className="text-sm font-medium text-grayscale-1300">{sub.summary}</p>}
            {sub.description && <p className="text-[13px] text-grayscale-1200 whitespace-pre-line leading-relaxed">{sub.description}</p>}
            <H>Parameters</H>
            <ParamTable params={sub.params} />
            <H>Example</H>
            <ExampleView example={sub.example} />
          </>
        )}
        {hasTrace && !thisSubStep && (
          <div className="text-xs text-grayscale-1100 italic">This run has no record for this sub-step.</div>
        )}
      </div>
    );
  }

  // --- a node ----------------------------------------------------------------
  return (
    <div className="flex flex-col gap-3">
      {header(String(node.kind), node.lane, node.label, node.code)}
      {toggle}
      {offPath && <div className="text-xs rounded-lg border border-grayscale-300 bg-grayscale-200 px-2.5 py-1.5 text-grayscale-1200">
        This run did not pass through this step.</div>}
      {showRun ? (
        <div className="flex flex-col gap-3">
          {ownSteps.map((st, i) => (
            <div key={i} className="flex flex-col gap-2">
              {ownSteps.length > 1 && <H>Visit {i + 1}</H>}
              <StepView step={st} />
            </div>
          ))}
          {subStepList.length > 0 && (
            <>
              <H>Sub-steps in this run</H>
              <div className="flex flex-col gap-1.5">
                {subStepList.map(({ s, step }) => {
                  const open = !!openSubs[s.id];
                  return (
                    <div key={s.id} className="rounded-lg border border-grayscale-300">
                      <button type="button" onClick={() => setOpenSubs(o => ({ ...o, [s.id]: !open }))}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-grayscale-200 rounded-lg">
                        <span className="text-grayscale-1100 text-xs w-3">{open ? '▾' : '▸'}</span>
                        <span className="text-sm font-medium text-grayscale-1300 flex-1 truncate">{s.label}</span>
                        <StatusBadge status={step.status} />
                        <span className="text-xs text-grayscale-1200 w-16 text-right">{fmtMs(step.ms)}</span>
                      </button>
                      {open && <div className="px-2.5 pb-2.5"><StepView step={step} compact /></div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {node.summary && <p className="text-sm font-medium text-grayscale-1300">{node.summary}</p>}
          {node.description && <p className="text-[13px] text-grayscale-1200 whitespace-pre-line leading-relaxed">{node.description}</p>}
          <H>Parameters</H>
          <ParamTable params={node.params} />
          {(node.substeps || []).length > 0 && (
            <>
              <H right={<span className="text-[11px] text-grayscale-1100">{String(node.kind) === 'parallel' ? 'run in parallel' : ''}</span>}>
                Sub-steps ({node.substeps!.length})
              </H>
              <div className="flex flex-col gap-1.5">
                {node.substeps!.map(s => {
                  const open = !!openSubs[s.id];
                  return (
                    <div key={s.id} className="rounded-lg border border-grayscale-300">
                      <div className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-grayscale-200 rounded-lg">
                        <button type="button" onClick={() => setOpenSubs(o => ({ ...o, [s.id]: !open }))}
                          className="flex items-center gap-2 flex-1 min-w-0 text-left">
                          <span className="text-grayscale-1100 text-xs w-3">{open ? '▾' : '▸'}</span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-grayscale-1300">{s.label}</span>
                            {s.summary && <span className="block text-[11.5px] text-grayscale-1100 leading-snug">{s.summary}</span>}
                          </span>
                        </button>
                        <button type="button" onClick={() => onSelect({ kind: 'node', id: node.id, sub: s.id })}
                          className="text-[11px] text-primary-700 underline underline-offset-2 shrink-0">open</button>
                      </div>
                      {open && (
                        <div className="px-2.5 pb-2.5 flex flex-col gap-2">
                          {s.description && <p className="text-[12.5px] text-grayscale-1200 whitespace-pre-line">{s.description}</p>}
                          <ParamTable params={s.params} />
                          {s.example && <ExampleView example={s.example} />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <H>Example</H>
          <ExampleView example={node.example} />
        </>
      )}
      {(out.length > 0 || inc.length > 0) && (
        <>
          <H>Connections</H>
          <div className="flex flex-col gap-1 text-[12px]">
            {out.map(e => (
              <EdgeRow key={e.id} dir="to" other={nodeById.get(e.to)} edge={e}
                onNode={() => onSelect({ kind: 'node', id: e.to })} onEdge={() => onSelect({ kind: 'edge', id: e.id })} />
            ))}
            {inc.map(e => (
              <EdgeRow key={e.id} dir="from" other={nodeById.get(e.from)} edge={e}
                onNode={() => onSelect({ kind: 'node', id: e.from })} onEdge={() => onSelect({ kind: 'edge', id: e.id })} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const EdgeRow: React.FC<{
  dir: 'to' | 'from'; other?: FlowNode; edge: { id: string; label?: string; condition?: string; kind?: string; to: string; from: string };
  onNode: () => void; onEdge: () => void;
}> = ({ dir, other, edge, onNode, onEdge }) => (
  <div className="flex items-start gap-1.5">
    <span className={`shrink-0 mt-px text-[10px] rounded px-1 ${edge.kind === 'fallback' ? 'bg-red-50 text-red-700' : 'bg-grayscale-200 text-grayscale-1100'}`}>
      {dir === 'to' ? '→' : '←'}
    </span>
    <span className="min-w-0">
      <button type="button" onClick={onNode} className="text-primary-700 hover:underline font-medium">
        {other?.label || (dir === 'to' ? edge.to : edge.from)}
      </button>
      {(edge.label || edge.condition) && (
        <button type="button" onClick={onEdge} className="text-grayscale-1100 hover:underline text-left">
          {' '}· {edge.label}{edge.condition && edge.label ? ' — ' : ''}{edge.condition}
        </button>
      )}
    </span>
  </div>
);

export default React.memo(FlowNodePanel);
